import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import Toggle from '../../../../components/Toggle';
import { TOGGLE_ROWS } from '../../../../constants/additionalOptions';
import useAdditionalOptions from '../../hooks/useAdditionalOptions';
import './style.scss';

export default function AdditionalOptions() {
  const { watch, setValue, formState: { errors } = {} } = useFormContext() || {};

  const formImportantNotes = watch ? (watch('importantNotes') || watch('termCond') || '') : '';
  const formDeliveryReq = watch ? watch('bookingAddressRequired') : false;
  const rawExtraFields = watch ? watch('extraFields') : [];
  const formExtraFields = Array.isArray(rawExtraFields)
    ? rawExtraFields
    : (rawExtraFields?.list || []);
  const formEnableSafeLinks = watch ? Boolean(watch('enableSafeLinks')) : false;

  const initialExtLinks = Array.isArray(formExtraFields)
    ? formExtraFields.map((link, idx) => ({
        id: link.id || `extlink-init-${idx}`,
        name: link.name || '',
        url: link.value || link.url || '',
      }))
    : [];

  const initialData = {
    toggles: {
      importantNotes: Boolean(formImportantNotes && formImportantNotes.trim() !== ''),
      externalLinks: Boolean(initialExtLinks.length > 0),
      requestDeliveryDetails: Boolean(formDeliveryReq),
    },
    extLinks: initialExtLinks,
  };

  const {
    expanded,
    setExpanded,
    toggles,
    handleToggle,
    extLinks,
    addExternalLink,
    removeExternalLink,
    updateExternalLink,
  } = useAdditionalOptions(initialData);

  useEffect(() => {
    if (setValue) {
      setValue('bookingAddressRequired', Boolean(toggles.requestDeliveryDetails));
    }
  }, [toggles.requestDeliveryDetails, setValue]);

  useEffect(() => {
    if (setValue) {
      const extraFieldsPayload = toggles.externalLinks
        ? extLinks.map((link) => ({
            name: link.name || '',
            value: link.url || '',
          }))
        : [];
      setValue('extraFields', extraFieldsPayload, { shouldValidate: true });
    }
  }, [extLinks, toggles.externalLinks, setValue]);

  const handleNotesChange = (e) => {
    const val = e.target.value;
    if (setValue) {
      setValue('importantNotes', val);
      setValue('termCond', val);
    }
  };

  const handleToggleClick = (key, next) => {
    handleToggle(key, next);
    if (key === 'importantNotes' && !next && setValue) {
      setValue('importantNotes', '');
      setValue('termCond', '');
    }
  };

  return (
    <div className="additional-options">
      <button
        type="button"
        className="additional-options__header"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <span className="additional-options__title">Additional options</span>
        <svg
          className={`additional-options__chevron ${
            expanded ? 'additional-options__chevron--up' : ''
          }`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 10L8 6L12 10"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div
        className="additional-options__body"
        style={{ display: expanded ? 'block' : 'none' }}
      >
        {TOGGLE_ROWS.map((row) => (
          <div className="option-row" key={row.key}>
            <div className="option-row__top">
              <div className="option-row__text">
                <div className="option-row__title">{row.title}</div>
                {row.description && (
                  <div className="option-row__description">
                    {row.description}
                  </div>
                )}
              </div>
              <Toggle
                checked={toggles[row.key]}
                onChange={(next) => handleToggleClick(row.key, next)}
                label={row.title}
              />
            </div>

            {/* Important notes to buyers */}
            {row.key === 'importantNotes' && toggles.importantNotes && (
              <div className="option-row__body-content">
                <textarea
                  className="notes-textarea"
                  placeholder="Notes buyers will see before ordering…"
                  value={formImportantNotes}
                  onChange={handleNotesChange}
                />
              </div>
            )}

            {/* External Links */}
            {row.key === 'externalLinks' && toggles.externalLinks && (
              <div className="option-row__body-content">
                <div className="extlinks-list">
                  {extLinks.map((link, idx) => {
                    const nameError = errors?.extraFields?.[idx]?.name?.message;
                    const urlError = errors?.extraFields?.[idx]?.value?.message;

                    return (
                      <div className="extlink-item-wrap" key={link.id}>
                        <div className="extlink-row">
                          <input
                            className={`extlink-name-input ${
                              nameError ? 'extlink-name-input--error' : ''
                            }`}
                            type="text"
                            placeholder="Label (e.g. Telegram)"
                            value={link.name}
                            onChange={(e) =>
                              updateExternalLink(link.id, 'name', e.target.value)
                            }
                          />
                          <input
                            className={`extlink-url-input ${
                              urlError ? 'extlink-url-input--error' : ''
                            }`}
                            type="url"
                            placeholder="https://…"
                            value={link.url}
                            onChange={(e) =>
                              updateExternalLink(link.id, 'url', e.target.value)
                            }
                          />
                          <button
                            type="button"
                            className="del-del-btn"
                            onClick={() => removeExternalLink(link.id)}
                            aria-label="Remove link"
                          >
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                        {(nameError || urlError) && (
                          <div className="extlink-error-group">
                            {nameError && (
                              <span className="extlink-error-msg">{nameError}</span>
                            )}
                            {urlError && (
                              <span className="extlink-error-msg">{urlError}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: '6px' }}>
                  <div
                    className="add-tier-link"
                    onClick={addExternalLink}
                    role="button"
                    tabIndex={0}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 1v10M1 6h10" />
                    </svg>
                    Add link
                  </div>
                </div>

                <label className="extlink-share-toggle">
                  <input
                    type="checkbox"
                    checked={formEnableSafeLinks}
                    onChange={(e) => setValue && setValue('enableSafeLinks', e.target.checked)}
                  />
                  <span>
                    Let buyers copy or preview links instead of opening them directly.
                  </span>
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
